import { Select, Table } from "antd";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/slices/auth.slice";
import { fetchGroupBranchClasses } from "../../../services/montessoriCourseBoard.service";
import useGroupBranchesByType from "../useGroupBranchesByType";
import useGroupBranchSwitch from "../useGroupBranchSwitch";

/**
 * Pick one Junior school, see its classes, open a class's board.
 *
 * The class table copies what a branch admin sees (Class, then "Click to view").
 * It cannot reuse ClassBoardPicker, which calls /classes — that only ever returns
 * the school you are currently acting in.
 *
 * Choosing a school only loads its classes. Nothing switches until a class is
 * clicked: that switches into the branch (the same call the Senior page's "View
 * Course Board" makes) and opens that class's own board, with editing.
 */
function GroupBranchClasses() {
  const user = useSelector(selectUser);
  const groupId = user?.group;
  const { options, isLoading: loadingBranches } = useGroupBranchesByType(true);
  const { goToBranch, switching } = useGroupBranchSwitch();
  const [schoolId, setSchoolId] = useState(undefined);

  // A group with a single Junior branch has nothing to choose between.
  useEffect(() => {
    if (!schoolId && options.length === 1) setSchoolId(options[0].value);
  }, [options, schoolId]);

  const classes = useQuery(
    ["montessori-group-branch-classes", groupId, schoolId],
    () => fetchGroupBranchClasses(groupId, schoolId),
    { enabled: !!groupId && !!schoolId },
  );

  const columns = [
    {
      title: "Class",
      align: "center",
      render: (record) => (
        <div style={{ color: "#111111" }} className="text-base font-semibold">
          {record?.name}
        </div>
      ),
      sorter: (a, b) => `${a?.name}`.localeCompare(`${b?.name}`),
    },
    {
      title: "Course Board",
      align: "center",
      render: (record) => (
        <div
          className="text-base font-semibold text-blue-500 cursor-pointer"
          onClick={() =>
            goToBranch(
              schoolId,
              `/dashboard/admin/menu/course-board/class/${record?._id}`,
            )
          }
        >
          Click to view
        </div>
      ),
    },
  ];

  if (!loadingBranches && !options.length) {
    return (
      <div className="bg-white p-8 rounded-xl text-gray-400">
        No Junior branches in this group yet.
      </div>
    );
  }

  return (
    <div
      style={{ borderRadius: "10px", color: "#616161" }}
      className="bg-white drop-shadow-md p-8 w-full"
    >
      <div className="flex items-center gap-3 pb-6">
        <span className="text-sm font-semibold">School</span>
        <Select
          showSearch
          style={{ width: 320 }}
          placeholder="Select a school"
          options={options}
          loading={loadingBranches}
          value={schoolId}
          onChange={setSchoolId}
          optionFilterProp="label"
        />
      </div>

      {schoolId ? (
        <Table
          columns={columns}
          dataSource={classes.data ?? []}
          rowKey={(record) => record._id}
          loading={classes.isLoading || switching}
          pagination={{ position: ["bottomCenter"] }}
          locale={{ emptyText: "No classes in this school yet" }}
        />
      ) : (
        <div className="text-gray-400 py-6">
          Choose a school to see its classes.
        </div>
      )}
    </div>
  );
}

export default GroupBranchClasses;
